import React, { useState } from 'react';
import { FileText, Folder, Search, Download, ExternalLink, Calendar, ShieldCheck, Info, Filter, LayoutGrid, List } from 'lucide-react';

export default function Transparencia() {
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [searchQuery, setSearchQuery] = useState('');

  const categorias = ['Todos', 'Institucional', 'Financiero', 'Legal', 'Electoral', 'Asambleas'];

  const documentos = [
    { id: 1, titulo: 'Estatuto Social 2024', categoria: 'Institucional', fecha: '2024-01-15', size: '2.4 MB', tipo: 'PDF' },
    { id: 2, titulo: 'Memoria y Balance General 2023', categoria: 'Financiero', fecha: '2024-03-10', size: '5.8 MB', tipo: 'PDF' },
    { id: 3, titulo: 'Informe de Auditoría Externa', categoria: 'Financiero', fecha: '2024-04-02', size: '1.1 MB', tipo: 'PDF' },
    { id: 4, titulo: 'Reglamento de Créditos v5', categoria: 'Legal', fecha: '2023-11-20', size: '850 KB', tipo: 'PDF' },
    { id: 5, titulo: 'Convocatoria Asamblea Ordinaria', categoria: 'Asambleas', fecha: '2024-02-28', size: '450 KB', tipo: 'PDF' },
    { id: 6, titulo: 'Manual de Prevención (ALA/CFT)', categoria: 'Legal', fecha: '2024-01-05', size: '3.2 MB', tipo: 'PDF' },
    { id: 7, titulo: 'Listado de Candidatos 2024', categoria: 'Electoral', fecha: '2024-05-01', size: '1.2 MB', tipo: 'PDF' },
  ];

  const filteredDocs = documentos.filter(doc => 
    (selectedCategory === 'Todos' || doc.categoria === selectedCategory) &&
    doc.titulo.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in duration-700">
      {/* Header Estratégico */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-[2.5rem] p-10 mb-10 text-white shadow-2xl relative overflow-hidden">
        <div className="absolute right-0 top-0 p-12 opacity-10">
          <ShieldCheck className="w-48 h-48" />
        </div>
        <div className="relative z-10 max-w-2xl">
          <h1 className="text-4xl font-black mb-4 flex items-center gap-4">
             Portal de Transparencia
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed">
            Acceso libre a documentos institucionales, estados financieros y normativas vigentes. 
            Nuestro compromiso es la claridad absoluta en la gestión de tu capital.
          </p>
          <div className="mt-8 flex items-center gap-4 text-xs font-bold uppercase tracking-widest bg-white/10 w-fit px-4 py-2 rounded-full border border-white/20">
             <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
             Información Actualizada al {new Date().toLocaleDateString()}
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar Categorías */}
        <div className="w-full lg:w-64 space-y-2">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 ml-4">Categorías</p>
          {categorias.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`w-full flex items-center gap-3 px-6 py-4 rounded-2xl font-black text-sm transition-all ${
                selectedCategory === cat 
                ? 'bg-slate-900 text-white shadow-lg shadow-slate-200' 
                : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              <Folder className={`w-5 h-5 ${selectedCategory === cat ? 'text-indigo-400' : 'text-slate-300'}`} />
              {cat}
            </button>
          ))}
          
          <div className="mt-10 p-6 bg-blue-50 rounded-3xl border border-blue-100">
             <div className="bg-blue-600 w-10 h-10 rounded-xl flex items-center justify-center mb-4 text-white shadow-lg shadow-blue-100">
               <Info className="w-5 h-5" />
             </div>
             <h4 className="text-sm font-black text-blue-900 mb-2 uppercase tracking-tight">¿Buscas algo más?</h4>
             <p className="text-xs text-blue-700 leading-relaxed font-medium">Si no encuentras un documento, puedes solicitarlo formalmente a la Secretaría de la Cooperativa.</p>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1">
          {/* Toolbar */}
          <div className="bg-white rounded-3xl border border-slate-100 p-4 mb-8 flex flex-col md:flex-row items-center gap-4 shadow-sm">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" />
              <input
                type="text"
                placeholder="Buscar por título de documento..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-50 border-none rounded-2xl pl-12 pr-4 py-3 text-sm font-bold focus:ring-2 focus:ring-slate-900 transition-all"
              />
            </div>
            <div className="flex items-center gap-2 border-l border-slate-100 pl-4">
              <button 
                onClick={() => setViewMode('grid')}
                className={`p-3 rounded-xl transition-all ${viewMode === 'grid' ? 'bg-slate-900 text-white' : 'text-slate-400 hover:bg-slate-50'}`}
              >
                <LayoutGrid className="w-5 h-5" />
              </button>
              <button 
                onClick={() => setViewMode('list')}
                className={`p-3 rounded-xl transition-all ${viewMode === 'list' ? 'bg-slate-900 text-white' : 'text-slate-400 hover:bg-slate-50'}`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Documents Grid/List */}
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredDocs.map(doc => (
                <div key={doc.id} className="bg-white rounded-[2rem] border border-slate-100 p-6 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group relative">
                  <div className="flex justify-between items-start mb-6">
                    <div className="bg-slate-50 w-14 h-14 rounded-2xl flex items-center justify-center text-slate-400 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors">
                      <FileText className="w-8 h-8" />
                    </div>
                    <span className="bg-slate-100 text-slate-500 text-[9px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest border border-slate-200">
                      {doc.tipo}
                    </span>
                  </div>
                  <h3 className="text-lg font-black text-slate-900 leading-tight mb-2 group-hover:text-indigo-600 transition-colors">{doc.titulo}</h3>
                  <div className="flex items-center gap-3 text-slate-400 mb-6">
                    <Calendar className="w-3.5 h-3.5" />
                    <span className="text-[10px] font-bold">{new Date(doc.fecha).toLocaleDateString()}</span>
                    <span className="w-1 h-1 rounded-full bg-slate-200"></span>
                    <span className="text-[10px] font-bold">{doc.size}</span>
                  </div>
                  <div className="flex gap-2">
                    <button className="flex-1 bg-slate-50 text-slate-600 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-indigo-600 hover:text-white transition-all flex items-center justify-center gap-2">
                      <ExternalLink className="w-3 h-3" />
                      Vista Previa
                    </button>
                    <button className="bg-slate-900 text-white p-3 rounded-xl hover:bg-emerald-600 transition-all shadow-lg shadow-slate-100">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-[2rem] border border-slate-100 overflow-hidden shadow-sm">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    <th className="px-8 py-4">Documento</th>
                    <th className="px-8 py-4">Categoría</th>
                    <th className="px-8 py-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filteredDocs.map(doc => (
                    <tr key={doc.id} className="group hover:bg-slate-50/50 transition-colors">
                      <td className="px-8 py-5">
                        <div className="flex items-center gap-4">
                          <FileText className="w-5 h-5 text-slate-300" />
                          <div>
                            <p className="text-sm font-black text-slate-900">{doc.titulo}</p>
                            <p className="text-[10px] text-slate-400 font-bold">{doc.fecha} • {doc.size}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-5">
                        <span className="bg-indigo-50 text-indigo-600 text-[10px] font-black px-3 py-1.5 rounded-lg uppercase tracking-wider">
                          {doc.categoria}
                        </span>
                      </td>
                      <td className="px-8 py-5 text-right">
                        <div className="flex justify-end gap-2">
                          <button className="p-2 text-slate-400 hover:text-indigo-600 transition-colors"><ExternalLink className="w-4 h-4" /></button>
                          <button className="p-2 text-slate-400 hover:text-emerald-600 transition-colors"><Download className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {filteredDocs.length === 0 && (
            <div className="py-20 text-center">
               <div className="bg-slate-50 w-24 h-24 rounded-[3rem] flex items-center justify-center mx-auto mb-6 text-slate-200">
                 <Search className="w-10 h-10" />
               </div>
               <p className="text-slate-400 font-black uppercase tracking-widest">No se encontraron documentos en esta categoría</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
